import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * MarketTicker - Scrolling BBO display
 * Shows best bid/offer in a scrolling marquee
 */
const MarketTicker = ({
  wsUrl = "wss://amberhorizon.eu/ws/market_data",
  restUrl = "https://amberhorizon.eu/api/order_book",
  symbolOverride = null,
  pollMs = 4000,
  speedSeconds = 20,
  allInstruments = null, // Optional: array of all instrument symbols to initialize
}) => {
  // Track market data for all instruments
  const [instruments, setInstruments] = useState({});
  const [errMsg, setErrMsg] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const restTimer = useRef(null);
  const wsRetryTimer = useRef(null);
  const initializedRef = useRef(false);
  const lastInstrumentsRef = useRef(null);
  const initialDataFetchedRef = useRef(false);
  
  // Initialize all instruments with empty data on mount if allInstruments prop is provided
  // Only initialize once to avoid overwriting existing data
  useEffect(() => {
    if (allInstruments && Array.isArray(allInstruments) && allInstruments.length > 0) {
      // Check if allInstruments has actually changed
      const instrumentsKey = allInstruments.sort().join(',');
      if (lastInstrumentsRef.current === instrumentsKey && initializedRef.current) {
        // Same instruments, already initialized - don't re-initialize
        return;
      }
      
      setInstruments(prev => {
        // Only initialize if we don't have any instruments yet, or if we're missing some
        const hasAnyData = Object.keys(prev).length > 0;
        if (hasAnyData && initializedRef.current) {
          // Don't overwrite existing data, just add missing instruments
          const updated = { ...prev };
          allInstruments.forEach(symbol => {
            if (!updated[symbol]) {
              updated[symbol] = {
                symbol,
                bid: null,
                ask: null,
                lastPrice: null
              };
            }
          });
          return updated;
        } else {
          // First initialization - set all instruments
          const initialInstruments = {};
          allInstruments.forEach(symbol => {
            // Preserve existing data if available
            if (prev[symbol] && (prev[symbol].bid !== null || prev[symbol].ask !== null || prev[symbol].lastPrice !== null)) {
              initialInstruments[symbol] = prev[symbol];
            } else {
              initialInstruments[symbol] = {
                symbol,
                bid: null,
                ask: null,
                lastPrice: null
              };
            }
          });
          initializedRef.current = true;
          lastInstrumentsRef.current = instrumentsKey;
          return initialInstruments;
        }
      });
    }
  }, [allInstruments]);

  // --- helpers -------------------------------------------------------------

  const priceOf = useCallback((x) => {
    if (x == null) return NaN;
    if (typeof x === "number") return x;
    if (Array.isArray(x)) {
      const a = Number(x[0]);
      const b = Number(x[1]);
      const aDec = !Number.isInteger(a);
      const bDec = !Number.isInteger(b);
      if (aDec && !bDec) return a;
      if (bDec && !aDec) return b;
      if (a > 2000 && b <= 2000) return b;
      if (b > 2000 && a <= 2000) return a;
      return Math.min(a, b);
    }
    if (typeof x === "object") {
      if ("price" in x) return Number(x.price);
      if ("px" in x) return Number(x.px);
      if ("p" in x) return Number(x.p);
      if ("val" in x) return Number(x.val);
      if (x.price !== undefined) return Number(x.price);
    }
    return Number(x);
  }, []);

  const bestBidFrom = useCallback((bids) => {
    const arr = Array.isArray(bids) ? bids : [];
    const ps = arr.map(priceOf).filter((n) => Number.isFinite(n));
    if (!ps.length) return null;
    return Math.max(...ps);
  }, [priceOf]);

  const bestAskFrom = useCallback((asks) => {
    const arr = Array.isArray(asks) ? asks : [];
    const ps = arr.map(priceOf).filter((n) => Number.isFinite(n));
    if (!ps.length) return null;
    return Math.min(...ps);
  }, [priceOf]);

  const fmt = useCallback((n) => {
    if (!Number.isFinite(n)) return "-";
    if (Math.abs(n) >= 100) return n.toFixed(2).replace(/\.00$/, "");
    return n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  }, []);

  const pickSymbol = useCallback((obj) => {
    // Don't use symbolOverride - use actual symbol from message
    const symbol = obj?.symbol ||
      obj?.instrument ||
      obj?.name ||
      obj?.data?.symbol ||
      obj?.data?.instrument ||
      (obj?.bids?.[0]?.symbol) ||
      (obj?.asks?.[0]?.symbol) ||
      "BBO";
    
    return symbol;
  }, []);

  const handleIncoming = useCallback((msg) => {
    try {
      let payload = msg;
      
      // WS "book" message - extract data from nested structure
      if (msg && msg.type === "book" && (msg.data || msg.book)) {
        payload = msg.data || msg.book;
      }
      // Also handle messages that have data structure at root level (REST-like)
      // but don't have type: "book" - check if it looks like market data
      else if (msg && !msg.type && (msg.bids || msg.asks || msg.symbol)) {
        // This is a REST-like message structure, use it directly
        payload = msg;
      }
      // If message has data property but no type, check if data looks like market data
      else if (msg && msg.data && (msg.data.bids || msg.data.asks || msg.data.symbol)) {
        payload = msg.data;
      }
      
      // Extract symbol from payload
      const symbol = pickSymbol(payload);
      
      // Extract market data
      let bid = null;
      let ask = null;
      let lastPrice = null;

      // Extract last traded price if available
      if (payload && payload.last_traded_price !== undefined && payload.last_traded_price !== null) {
        lastPrice = priceOf(payload.last_traded_price);
      }

      // shape A: best_bid/best_offer
      if (payload && (payload.best_bid || payload.best_offer)) {
        bid = payload.best_bid ? priceOf(payload.best_bid) : null;
        ask = payload.best_offer ? priceOf(payload.best_offer) : null;
      }
      // shape B: bids/asks arrays
      else if (payload && (payload.bids || payload.asks)) {
        bid = bestBidFrom(payload.bids);
        ask = bestAskFrom(payload.asks);
      }

      // Update if we have a valid symbol (even if no bids/asks yet, to show the instrument)
      if (symbol && symbol !== "BBO") {
        // Use functional update to ensure we always work with latest state
        // This prevents race conditions when multiple messages arrive quickly
        setInstruments(prev => {
          // Always create a new object reference to ensure React detects the change
          const newInstrumentData = {
            symbol,
            bid: Number.isFinite(bid) ? bid : null,
            ask: Number.isFinite(ask) ? ask : null,
            lastPrice: Number.isFinite(lastPrice) ? lastPrice : null
          };
          
          // Create a new state object, preserving all other instruments
          // This ensures we don't lose updates from other symbols
          const updated = {
            ...prev,  // Preserve all existing instruments
            [symbol]: newInstrumentData  // Update only this symbol
          };
          
          return updated;
        });
        setErrMsg(null);
      }
    } catch (err) {
      console.error("[MarketTicker] parse error:", err);
      setErrMsg("Parse error");
    }
  }, [pickSymbol, priceOf, bestBidFrom, bestAskFrom]);

  // --- WebSocket connection ------------------------------------------------
  useEffect(() => {
    if (!wsUrl) return;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log("[MarketTicker] WS connected");
          setWsConnected(true);
          setErrMsg(null);
        };

        ws.onmessage = (evt) => {
          try {
            const parsed = JSON.parse(evt.data);
            handleIncoming(parsed);
          } catch (err) {
            console.error("[MarketTicker] WS parse error:", err);
          }
        };

        ws.onerror = (err) => {
          console.error("[MarketTicker] WS error:", err);
          setErrMsg("WebSocket error");
        };

        ws.onclose = () => {
          console.log("[MarketTicker] WS closed, retry in 5s");
          setWsConnected(false);
          wsRetryTimer.current = setTimeout(connect, 5000);
        };
      } catch (err) {
        console.error("[MarketTicker] WS connect error:", err);
        setErrMsg("Connection failed");
        wsRetryTimer.current = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (wsRetryTimer.current) {
        clearTimeout(wsRetryTimer.current);
      }
    };
  }, [wsUrl, handleIncoming]);

  // --- Initial data fetch for all instruments ---------------------------------
  useEffect(() => {
    if (!restUrl || !allInstruments || allInstruments.length === 0) return;
    
    // Check if we've already fetched initial data for these instruments
    const instrumentsKey = allInstruments.sort().join(',');
    if (initialDataFetchedRef.current === instrumentsKey) return;

    // Fetch initial data for all instruments
    const fetchInitialData = async () => {
      const baseUrl = restUrl.replace('/api/order_book', '');
      const promises = allInstruments.map(async (symbol) => {
        try {
          const res = await fetch(`${baseUrl}/api/market_data?symbol=${symbol}`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          handleIncoming(data);
        } catch (err) {
          console.error(`[MarketTicker] Failed to fetch initial data for ${symbol}:`, err);
        }
      });
      await Promise.all(promises);
      initialDataFetchedRef.current = instrumentsKey;
    };

    fetchInitialData();
  }, [restUrl, allInstruments, handleIncoming]);

  // --- REST fallback (only when WebSocket is disconnected) --------------------
  useEffect(() => {
    if (!restUrl || wsConnected || !allInstruments || allInstruments.length === 0) return;

    const baseUrl = restUrl.replace('/api/order_book', '');
    
    const poll = async () => {
      // Fetch data for all instruments
      const promises = allInstruments.map(async (symbol) => {
        try {
          const res = await fetch(`${baseUrl}/api/market_data?symbol=${symbol}`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          handleIncoming(data);
        } catch (err) {
          console.error(`[MarketTicker] REST error for ${symbol}:`, err);
        }
      });
      await Promise.all(promises);
    };

    poll();
    restTimer.current = setInterval(poll, pollMs);

    return () => {
      if (restTimer.current) clearInterval(restTimer.current);
    };
  }, [restUrl, pollMs, wsConnected, allInstruments, handleIncoming]);

  // Get all instrument symbols - use allInstruments prop if provided, otherwise use instruments state
  // Always show all instruments from allInstruments prop, even if they don't have data yet
  const instrumentSymbols = allInstruments && allInstruments.length > 0 
    ? allInstruments
    : Object.keys(instruments);
  
  // Distribute instruments across 2 lines
  // First line gets extra item if odd number
  const firstLineCount = Math.ceil(instrumentSymbols.length / 2);
  const firstLineInstruments = instrumentSymbols.slice(0, firstLineCount);
  const secondLineInstruments = instrumentSymbols.slice(firstLineCount);

  // Create the ticker item component
  const TickerItem = ({ instrumentData }) => {
    if (!instrumentData) {
      return null;
    }
    
    const { symbol, bid, ask, lastPrice } = instrumentData;
    
    const lastStr = Number.isFinite(lastPrice) ? fmt(lastPrice) : "--";
    const bidStr = Number.isFinite(bid) ? fmt(bid) : "--";
    const askStr = Number.isFinite(ask) ? fmt(ask) : "--";
    
    return (
      <span style={{display: 'inline-flex', alignItems: 'center', gap: '12px', marginRight: '48px', fontSize: '13px', fontWeight: '600'}}>
        <span style={{color: 'var(--text)'}}>{symbol}</span>
        <span style={{color: '#FFD700'}}>L <span style={lastStr === "--" ? {color: '#666', fontStyle: 'italic'} : {}}>{lastStr}</span></span>
        <span style={{color: 'var(--good)'}}>B <span style={bidStr === "--" ? {color: '#666', fontStyle: 'italic'} : {}}>{bidStr}</span></span>
        <span style={{color: 'var(--bad)'}}>S <span style={askStr === "--" ? {color: '#666', fontStyle: 'italic'} : {}}>{askStr}</span></span>
      </span>
    );
  };
  
  // Render ticker line - pass instruments as prop to ensure it receives latest data
  const TickerLine = ({ instrumentSymbols: symbols, instrumentsData }) => {
    if (symbols.length === 0) {
      return (
        <span style={{display: 'inline-flex', alignItems: 'center', gap: '12px', marginRight: '48px', fontSize: '13px', fontWeight: '600', color: '#666'}}>
          No data
        </span>
      );
    }
    
    return (
      <>
        {[...Array(20)].map((_, setIndex) => (
          <React.Fragment key={`set-${setIndex}`}>
            {symbols.map((symbol, idx) => {
              // Get instrument data, or create empty data structure if not available
              const instrumentData = instrumentsData[symbol] || {
                symbol,
                bid: null,
                ask: null,
                lastPrice: null
              };
              return (
                <TickerItem key={`${symbol}-${setIndex}-${idx}`} instrumentData={instrumentData} />
              );
            })}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <div style={{overflow: 'hidden', borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--panel)', position: 'relative'}}>
      <div style={{display: 'flex', flexDirection: 'column', lineHeight: '2rem'}}>
        {/* First line */}
        <div style={{display: 'flex', overflow: 'hidden'}}>
          <div
            style={{
              display: 'inline-flex',
              whiteSpace: 'nowrap',
              padding: '0 24px',
              animation: `marquee-scroll ${speedSeconds}s linear infinite`,
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)'
            }}
          >
            <TickerLine instrumentSymbols={firstLineInstruments} instrumentsData={instruments} />
          </div>
          <div
            style={{
              display: 'inline-flex',
              whiteSpace: 'nowrap',
              padding: '0 24px',
              animation: `marquee-scroll ${speedSeconds}s linear infinite`,
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)'
            }}
          >
            <TickerLine instrumentSymbols={firstLineInstruments} instrumentsData={instruments} />
          </div>
        </div>
        
        {/* Second line */}
        {secondLineInstruments.length > 0 && (
          <div style={{display: 'flex', overflow: 'hidden', marginTop: '-1rem'}}>
            <div
              style={{
                display: 'inline-flex',
                whiteSpace: 'nowrap',
                padding: '0 24px',
                animation: `marquee-scroll ${speedSeconds}s linear infinite`,
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)'
              }}
            >
              <TickerLine instrumentSymbols={secondLineInstruments} instrumentsData={instruments} />
            </div>
            <div
              style={{
                display: 'inline-flex',
                whiteSpace: 'nowrap',
                padding: '0 24px',
                animation: `marquee-scroll ${speedSeconds}s linear infinite`,
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)'
              }}
            >
              <TickerLine instrumentSymbols={secondLineInstruments} instrumentsData={instruments} />
            </div>
          </div>
        )}
      </div>

      {errMsg && (
        <div style={{padding: '4px 12px', fontSize: '11px', color: 'var(--bad)', background: 'rgba(229, 83, 83, 0.1)', borderTop: '1px solid var(--border)'}}>
          {errMsg}
        </div>
      )}

      <style>{`
        @keyframes marquee-scroll {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(-100%, 0, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default MarketTicker;
